import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../../state/authStore';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import PageHeader from '../../../components/layout/PageHeader';
import { Loader2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const COLS = ['backlog', 'researching', 'under_review', 'conviction', 'passed'];

export default function ResearchBoardPage() {
  const user = useAuthStore(s => s.user);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState('');
  const [cards, setCards] = useState([]);
  const [newBoard, setNewBoard] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${BACKEND}/api/orgs`).then(({ data }) => {
      setOrgs(Array.isArray(data) ? data : []);
      if (data?.[0]?.id) setOrgId(data[0].id);
    }).catch(() => setOrgs([]));
  }, [user?.id]);

  const loadBoards = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/research-boards`, { params: { org_id: orgId } });
      setBoards(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadBoards(); }, [loadBoards]);

  const loadCards = useCallback(async () => {
    if (!boardId) {
      setCards([]);
      return;
    }
    const { data } = await axios.get(`${BACKEND}/api/research-boards/${boardId}/cards`);
    setCards(Array.isArray(data) ? data : []);
  }, [boardId]);

  useEffect(() => { loadCards(); }, [loadCards]);

  async function createBoard(e) {
    e.preventDefault();
    if (!orgId || !newBoard.trim()) return;
    await axios.post(`${BACKEND}/api/research-boards`, { org_id: orgId, name: newBoard.trim() });
    setNewBoard('');
    loadBoards();
  }

  async function moveCard(card, col) {
    await axios.patch(`${BACKEND}/api/research-boards/cards/${card.id}`, { column_name: col });
    loadCards();
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Research boards" subtitle="Kanban pipeline for team coverage" />
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-muted-foreground">Organization</label>
          <select
            className="block rounded-md border bg-background px-3 py-2 text-sm mt-1"
            value={orgId}
            onChange={e => { setOrgId(e.target.value); setBoardId(''); }}
          >
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Board</label>
          <select
            className="block rounded-md border bg-background px-3 py-2 text-sm mt-1 min-w-[200px]"
            value={boardId}
            onChange={e => setBoardId(e.target.value)}
          >
            <option value="">Select…</option>
            {boards.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <form onSubmit={createBoard} className="flex gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground">New board</label>
            <Input className="mt-1 h-9" value={newBoard} onChange={e => setNewBoard(e.target.value)} placeholder="Name" />
          </div>
          <Button type="submit" size="sm" variant="secondary">Create</Button>
        </form>
      </div>

      {!boardId && (
        <p className="text-sm text-muted-foreground">Select a board to view columns.</p>
      )}

      {loading && <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />}

      {boardId && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 overflow-x-auto pb-4">
          {COLS.map(col => (
            <Card key={col} className="min-h-[320px] bg-card/40">
              <CardContent className="pt-3">
                <p className="text-xs font-semibold text-emerald-500 mb-2 capitalize">{col.replace('_', ' ')}</p>
                <div className="space-y-2">
                  {cards.filter(c => c.column_name === col).map(c => (
                    <div
                      key={c.id}
                      className="rounded-md border border-border/60 bg-background/80 p-2 text-xs space-y-1"
                    >
                      <p className="font-medium">{c.symbol || c.isin}</p>
                      {c.company_name && <p className="text-muted-foreground truncate">{c.company_name}</p>}
                      <div className="flex flex-wrap gap-1 pt-1">
                        {COLS.filter(x => x !== col).slice(0, 3).map(x => (
                          <button
                            key={x}
                            type="button"
                            className="px-1.5 py-0.5 rounded bg-muted text-[10px]"
                            onClick={() => moveCard(c, x)}
                          >→ {x}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
